/**
 * Shared state core — the single source of truth for state-bearing primitives
 * and the canonical slug/URL algorithm.
 *
 * Consumed by all three former copies of the slug logic:
 *   - lib/slugs.ts            (app/router/SEO — re-exports from here)
 *   - scripts/slug-utils.mjs  (generator scripts — re-exports from here)
 *   - vite.config.ts          (SSG route classification — imports from here)
 *
 * Runtime duality contract: this file is plain ESM JavaScript with ZERO
 * dependencies so plain-Node .mjs scripts can import it with no build step;
 * TypeScript consumers get full typing from the adjacent stateCore.d.mts.
 * The one import below is a GENERATED plain-ESM data module, which keeps that
 * contract intact — no package, no build step, importable by Node and Vite alike.
 *
 * Behaviour contract: byte-identical output to the three prior copies for all
 * existing (state-less) call shapes — locked by lib/stateCore.test.ts golden
 * fixtures. Every state parameter defaults to 'nsw'.
 */

import { SUBURB_POSTCODES } from '../data/suburbPostcodes.mjs';

// ============================================
// STATE PRIMITIVES
// ============================================

/**
 * Valid Australian state/territory URL slugs (lowercase). Promoted from the
 * STATE_ABBREVIATIONS sets formerly duplicated in vite.config.ts,
 * lib/slugs.ts, and scripts/slug-utils.mjs. Doubles as the token-strip set
 * inside toLocationSlug ("Sydney NSW 2000" → "sydney").
 */
export const STATE_SLUGS = new Set(['nsw', 'vic', 'qld', 'sa', 'wa', 'tas', 'nt', 'act']);

/** Legacy alias — scripts/slug-utils.mjs exported the set under this name. */
export const STATE_ABBREVIATIONS = STATE_SLUGS;

/**
 * States the platform has LAUNCHED (routes generated, sitemaps emitted,
 * runtime allowlist). This is the future runtime switch: defined here in R1,
 * enforcement wired in later runs. Currently exactly ['nsw'].
 */
export const LAUNCHED_STATES = ['nsw', 'vic'];

/** Default state for every parameterised call — keeps all existing call sites behaviour-identical. */
export const DEFAULT_STATE = 'nsw';

/** State slug → display form: 'nsw' → 'NSW'. Pure case mapping (all AU codes uppercase cleanly). */
export function stateSlugToDisplay(stateSlug) {
    return String(stateSlug).toUpperCase();
}

/** State display form → slug: 'NSW' → 'nsw'. Pure case mapping. */
export function stateDisplayToSlug(stateDisplay) {
    return String(stateDisplay).toLowerCase();
}

// ============================================
// SLUG CONSTANTS (carried verbatim from the prior copies)
// ============================================

/** Reserved words that cannot be used as slugs - will be prefixed with "place-" */
export const RESERVED_WORDS = new Set([
    'gyms', 'nsw', 'admin', 'api', 'login', 'account', 'search', 'sitemap'
]);

/** Noise tokens to remove from end of gym names */
export const GYM_TRAILING_NOISE = ['pty-ltd', 'pty', 'ltd', 'australia', 'nsw'];

/** Maximum slug length (truncate at hyphen boundaries) */
export const MAX_SLUG_LENGTH = 60;

// ============================================
// STYLE VOCABULARY (D3: FROZEN)
// ============================================

/**
 * Canonical style vocabulary — verbatim copy of lib/filters.ts STYLE_OPTIONS
 * (the platform's single UI source of truth), carried here so the slug layer
 * and generator scripts share one list. FROZEN by R1 decision D3: no
 * additions, removals, or renames. Parity with lib/filters.ts is locked by
 * lib/stateCore.test.ts — if the two ever diverge, that test fails.
 */
export const CANONICAL_STYLES = Object.freeze([
    'Aikido',
    'Boxing',
    'BJJ',
    'Capoeira',
    'Hapkido',
    'Judo',
    'Karate',
    'Kendo',
    'Kickboxing',
    'Krav Maga',
    'Kung Fu',
    'MMA',
    'Muay Thai',
    'Taekwondo',
    'Wrestling',
]);

// ============================================
// CORE NORMALISATION (algorithm carried verbatim; see stateCore.test.ts)
// ============================================

/**
 * Core slug normalisation pipeline.
 *
 * Steps applied in order:
 * 1) Trim and lowercase
 * 2) Unicode normalise (NFKD) and strip diacritics
 * 3) Replace "&" with "and"
 * 4) Remove apostrophes and quotes
 * 5) Replace all non-alphanumeric characters with hyphens
 * 6) Collapse multiple hyphens into one
 * 7) Trim leading and trailing hyphens
 */
export function normaliseSlug(input) {
    if (!input) return '';
    return input.trim().toLowerCase()
        .normalize('NFKD').replace(/[̀-ͯ]/g, '')
        .replace(/&/g, 'and').replace(/[''"`´]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Truncate slug to max length at hyphen boundaries. Never cuts mid-token.
 */
export function truncateAtHyphenBoundary(slug, maxLength) {
    if (slug.length <= maxLength) return slug;
    const truncated = slug.substring(0, maxLength);
    const lastHyphen = truncated.lastIndexOf('-');
    return lastHyphen > 0 ? truncated.substring(0, lastHyphen) : truncated;
}

/**
 * Apply reserved word safety - prefix with "place-" if slug is reserved.
 */
export function applyReservedWordSafety(slug) {
    return RESERVED_WORDS.has(slug) ? `place-${slug}` : slug;
}

/**
 * Remove noise tokens from the end of a gym slug.
 * Also removes leading "the" if present.
 */
export function removeGymNoise(slug) {
    let tokens = slug.split('-');
    if (tokens.length > 1 && tokens[0] === 'the') tokens = tokens.slice(1);
    let changed = true;
    while (changed && tokens.length > 1) {
        changed = false;
        const last = tokens[tokens.length - 1];
        if (GYM_TRAILING_NOISE.includes(last)) { tokens.pop(); changed = true; continue; }
        if (tokens.length >= 2 && tokens.slice(-2).join('-') === 'pty-ltd') { tokens = tokens.slice(0, -2); changed = true; }
    }
    return tokens.join('-');
}

// ============================================
// SLUG GENERATION
// ============================================

/**
 * Canonicalise a suburb/locality name to the spelling used by the official
 * locality register — a LEADING "Saint" is written "St" ("Saint Marys" →
 * "St Marys"). Idempotent: a name already in register form is returned as-is.
 *
 * Why this exists (audit H1/M1). Gym records carry a free-text `suburb`, and
 * four NSW suburbs are recorded as "Saint …" while the register spells every
 * one of them "St …". Everything the site publishes as a LIST — the hub A–Z
 * and every sitemap — is pruned to names that match the register, while page
 * ROUTES are not pruned. So those four suburbs had real, populated, indexable
 * pages that appeared in no sitemap and in no A–Z entry: 20 indexable pages,
 * including 17 gym profiles, undiscoverable. Canonicalising the name makes the
 * two derivations agree at the source instead of papering over it downstream.
 *
 * ANCHORED TO THE START, deliberately. The register keeps a non-leading
 * "Saint" — "Mount Saint Thomas" (2500) is spelled exactly that way — so a
 * blanket "Saint"→"St" replace would corrupt it into a name that matches
 * nothing. Every one of the 17 NSW register localities containing "St"/"Saint"
 * obeys this split: leading is always "St", non-leading stays "Saint".
 * `assertSuburbCanonicalisationSafe` re-checks that against the live register
 * on every generator run so a future state cannot violate it silently.
 *
 * Accepts either a display name ("Saint Marys 2760") or an already-slugged
 * form ("saint-marys") — `\b` matches the hyphen boundary in the latter.
 */
export function canonicaliseSuburbName(name) {
    // Falsy in → empty out, matching normaliseSlug. Without this guard
    // String(undefined) would become the literal slug "undefined" and
    // toLocationSlug would stop falling back to the state slug.
    if (!name) return '';
    const saintFixed = String(name).replace(/^\s*saint\b/i, 'St');
    // Look the map up on the NAME ALONE. Callers legitimately pass "Suburb Postcode"
    // and "Suburb STATE Postcode" — buildGymUrl builds exactly that — so keying on the
    // raw string silently missed every one of them. Caught by checking the OUTPUT of
    // buildGymUrl rather than re-reading this function: it returned the unregistered
    // /nsw/west-lindfield/… address, which is the very thing F2 exists to prevent.
    // Candidates in MOST-SPECIFIC-FIRST order, and that order is load-bearing: one of
    // the four names IS "University of NSW", which ends in a state token. Stripping the
    // token before looking up turns it into "University of" and matches nothing — the
    // exact M2 trap this map exists to close, sprung by the fix for it. Try the whole
    // name first, then peel the postcode, then the state token.
    const lower = saintFixed.trim().toLowerCase();
    const noPostcode = lower.replace(/\s+\d{4}$/, '').trim();
    const noState = noPostcode.replace(/\s+(?:nsw|vic|qld|sa|wa|tas|nt|act)$/, '').trim();
    // Return the locality NAME only: the postcode that arrived with a wrong suburb
    // belongs to that wrong suburb, and toAddressedLocationSlug supplies the right one.
    return NON_REGISTER_LOCALITIES[lower]
        ?? NON_REGISTER_LOCALITIES[noPostcode]
        ?? NON_REGISTER_LOCALITIES[noState]
        ?? saintFixed;
}

/**
 * Suburb names Google returns that are NOT localities in the official register,
 * mapped to the locality the premises actually sit in (audit F2).
 *
 * WHY THIS EXISTS. `sync-display` takes the parsed suburb unfiltered for a gym it has
 * not seen before, so these names enter from Google and can be re-minted at any time.
 * They were corrected once by hand as one-off redirects in the Class B slice, but the
 * raw strings still sit in 8 upstream `enrichment` and 7 `discovery` records, so the
 * correction was downstream of the problem. Mapping them here puts the fix at the seam
 * every consumer already passes through — routes, sitemaps, the hub, and the functions
 * search comparison — so a recurrence lands on the right page instead of minting a new
 * unregistered one.
 *
 * HONEST LIMITATION, worth stating because the original decision was finer-grained than
 * this map. Class B resolved each GYM by distance from its own coordinates — "University
 * of NSW" went to Kensington because that gym is 0.88 km from it and 2033 is the sole
 * locality there. A name→name map cannot make a per-record judgement, so it is only safe
 * where the name denotes one small area with one sensible answer, which is true of all
 * four. It is deliberately NOT extended to the two other Class B names: "Hume" is an ACT
 * suburb (nearest NSW locality is across the border) and "Swans Crossing" is a campground
 * — neither has a correct NSW locality, and inventing one would put a non-NSW business
 * back on a NSW page. Those two stay excluded at the record level.
 *
 * Keys are lower-cased and trimmed; matching is exact, never a prefix, so "Lindfield"
 * and "East Lindfield" are untouched. Mirrored in functions/src/stateConfig.ts and
 * locked by lib/functionsStateMirror.test.ts.
 */
export const NON_REGISTER_LOCALITIES = Object.freeze({
    'west lindfield': 'Lindfield',
    'epping heights': 'Epping',
    'university of nsw': 'Kensington',
    'one mile beach': 'Forster',
});

/**
 * Guard for canonicaliseSuburbName's "leading Saint is always St" premise.
 *
 * The rule is derived from the NSW register, not from a general truth about
 * Australian place names, so it is re-checked against whichever register the
 * generators actually load. A locality whose OWN registered name starts with
 * "Saint" would be silently rewritten into a slug matching nothing — exactly
 * the orphaning this rule was written to remove, only in the other direction.
 *
 * Returns the offending localities (empty = safe). Callers log loudly; this is
 * deliberately not a throw, so a register quirk in an unlaunched state cannot
 * take down a build for a launched one.
 */
export function assertSuburbCanonicalisationSafe(localities) {
    return localities.filter(n => /^\s*saint\b/i.test(String(n || '')));
}

/**
 * Generate a clean location slug from a suburb name.
 * Strips postcodes (4-digit numbers) and state abbreviations.
 *
 * The name is canonicalised to register spelling FIRST (see
 * canonicaliseSuburbName), so the slug a page is built at and the slug the
 * register is keyed by are derived from one value and cannot drift apart.
 *
 * Fallback: returns the state slug ('nsw' by default) if empty after stripping.
 */
export function toLocationSlug(input, state = DEFAULT_STATE) {
    const slug = normaliseSlug(canonicaliseSuburbName(input));
    if (!slug) return state;
    const tokens = slug.split('-').filter(t => !/^\d{4}$/.test(t) && !STATE_SLUGS.has(t));
    const clean = tokens.join('-');
    if (!clean) return state;
    return truncateAtHyphenBoundary(applyReservedWordSafety(clean), MAX_SLUG_LENGTH);
}

/**
 * Generate a style slug from martial arts style name.
 * Example: "Muay Thai" → "muay-thai"
 */
export function toStyleSlug(style) {
    const slug = normaliseSlug(style);
    if (!slug) return '';
    return truncateAtHyphenBoundary(applyReservedWordSafety(slug), MAX_SLUG_LENGTH);
}

/**
 * Generate a gym name slug (without ID suffix).
 * Applies noise removal and length limits. Fallback: "gym".
 */
export function toGymNameSlug(name) {
    let slug = normaliseSlug(name);
    if (!slug) return 'gym';
    slug = removeGymNoise(slug);
    if (!slug) return 'gym';
    return truncateAtHyphenBoundary(applyReservedWordSafety(slug), MAX_SLUG_LENGTH);
}

/**
 * Generate a gym URL slug with embedded ID.
 * Uses the stable internal ID (not Google Place ID) for URL stability.
 */
export function toGymSlug(name, id) {
    return `${toGymNameSlug(name)}-g${id}`;
}

/** Style slug → canonical display name, derived from the frozen vocabulary. */
export const STYLE_SLUG_TO_NAME = Object.freeze(
    Object.fromEntries(CANONICAL_STYLES.map((name) => [toStyleSlug(name), name]))
);

// ============================================
// ADDRESSED LOCATION SLUGS (audit O6 — the postcode in the URL)
// ============================================

/**
 * Bare location slug → the postcode that suburb's pages are ADDRESSED at.
 *
 * Starts from the generated data/suburbPostcodes.mjs so a plain `import` of this
 * module — from Node, from Vite, or from the browser — already knows every address
 * without a bootstrap step. scripts/generate-ssg-routes.mjs overrides it in-process
 * (see setSuburbPostcodes) with the map it is about to write, so the run that MINTS
 * the routes and the run that RECORDS their postcodes can never be one build apart.
 */
let suburbPostcodes = SUBURB_POSTCODES;

/**
 * Replace the address map for this process. Used by the route generator, which
 * resolves the map from live Firestore and must build routes from THAT, not from
 * the previous build's file. Pass a `{ state: { slug: postcode } }` object.
 */
export function setSuburbPostcodes(map) {
    suburbPostcodes = map || {};
}

/** The postcode a suburb's pages are addressed at, or '' when the map has no entry. */
export function getSuburbPostcode(bareSlug, state = DEFAULT_STATE) {
    const forState = suburbPostcodes && suburbPostcodes[state];
    const pc = forState ? forState[bareSlug] : undefined;
    return /^\d{4}$/.test(String(pc || '')) ? String(pc) : '';
}

/**
 * The slug a location's pages are actually built and served at: the bare slug with
 * this suburb's postcode appended ("adamstown" → "adamstown-2289").
 *
 * WHY THIS IS A SUFFIX RATHER THAN A CHANGE TO toLocationSlug. The postcode-stripping
 * inside toLocationSlug is a NORMALISER, not an oversight: it is the reason ~19
 * heterogeneous call sites agree on one slug. Some hold "Suburb Postcode"
 * (LandingPage), most hold a bare name (App.tsx, breadcrumbs, GymProfilePage,
 * DirectoryPage), and several structurally CANNOT hold a postcode — a saved search
 * stores a location string; a breadcrumb has only a display label. Deleting the strip
 * would force every one of them to supply a postcode or silently emit the old address,
 * which is audit H1 exactly: pages built from one derivation, links from another.
 *
 * Keeping the strip and appending afterwards inverts that. Every caller's input —
 * bare name, "Suburb Postcode", or an already-addressed slug — normalises to the same
 * bare key, and the ONE correct postcode is then attached from the map. No call site
 * needs a new argument, and a caller holding only a name still emits the live address
 * rather than a 301 hop.
 *
 * A suburb the map does not cover keeps its bare slug. That is the safe degradation
 * for one unknown suburb and a catastrophe if the whole map is empty, so the
 * generator HARD-FAILS on collapsed coverage rather than quietly re-addressing the
 * entire corpus — the "a soft-fail hid a wrong input" lesson from the titles slice.
 */
export function toAddressedLocationSlug(input, state = DEFAULT_STATE) {
    const bare = toLocationSlug(input, state);
    const pc = getSuburbPostcode(bare, state);
    return pc ? `${bare}-${pc}` : bare;
}

// ============================================
// URL BUILDERS (parameterised; state defaults to 'nsw')
// ============================================

/**
 * Build a state's suburbs-hub URL — the tier-2 node of the breadcrumb spine.
 * Format: /[state]/suburbs (UI-57 flip, 2026-08-14: the index vacated
 * /[state]/gyms, which is now the state DIRECTORY root).
 *
 * Single source for the hub path: DEFAULT_STATE_HUB_PATH (lib/stateRoutes.ts)
 * and the breadcrumb spine (lib/breadcrumbs.ts) both derive from this, so the
 * state node's href cannot drift between the hub page and the gym trail — the
 * divergence that produced audit items #15/#19. ⚠️ This edit was SEQUENCED LAST
 * in the flip tranche by design: it is the arming line for the state-root mode
 * (stateDirectoryRootSlug), the footer/EmptyState/StateHubNav links, and the
 * breadcrumb spine, all of which derive from here.
 */
export function buildStateHubUrl(state = DEFAULT_STATE) {
    return `/${state}/suburbs`;
}

/**
 * The state DIRECTORY ROOT — '/{state}/gyms', the searchable directory that took the
 * hub's old address at the UI-57 flip (2026-08-14).
 *
 * This is the .mjs SINGLE SOURCE for that address on the generator side
 * (generate-ssg-routes / generate-sitemaps / indexnow-urls), added so the flip's
 * generator tranche derives it instead of retyping it — every retyped copy of a hub
 * address bit someone during the flip build.
 *
 * ⚠️ CROSS-BOUNDARY TWIN, deliberate: lib/stateRoutes.ts carries its own
 * `buildStateDirectoryRootUrl` because plain-ESM generators cannot import TypeScript
 * (the shellIdentity.mjs precedent). Two homes, one per language boundary — if this
 * value ever changes, change BOTH, and say so in both docblocks.
 */
export function buildStateDirectoryRootUrl(state = DEFAULT_STATE) {
    return `/${state}/gyms`;
}

/**
 * 🪦 `buildStateStyleUrl(style, state)` -> `/[state]/gyms/[style]` LIVED HERE. BACKLOG item 48's
 * SEO half: built 2026-08-16, shipped in deploy #26, REMOVED the same day by operator ruling.
 *
 * ⚠️⚠️ **THIS TOMBSTONE CANCELS A STANDING INSTRUCTION THE OLD DOCBLOCK CARRIED, and that is the
 * main reason it is this long.** The removed comment said: *"If the tier is ever withdrawn, the
 * redirects come back; do not leave the URLs serving the fallback shell silently."* **THAT IS NO
 * LONGER THE RULING AND MUST NOT BE FOLLOWED.** The operator was shown, twice and with the cost
 * measured, that removal is strictly worse than a redirect — `firebase.json` has no 404 handling,
 * so every unmatched path rewrites to `/notfound.html` and is served **HTTP 200**: the 30 URLs now
 * return a 73,801-byte fallback shell titled "Martial Arts Network" against the 26,658-byte
 * correctly-titled page they replaced. **They ruled removal with no redirect anyway.** A future
 * reader following the old instruction would restore 30 redirects against a live ruling.
 *
 * ⚠️ WHY THE TIER WENT AT ALL: not because it failed. It was gated, mutation-proved and verified
 * live. Asked what those URLs were FOR, the operator ruled they were never meant to be gym-listing
 * pages — they were meant to land the visitor on the DirectoryPage with the style filter applied,
 * awaiting a suburb. A prerendered page cannot be that. **The specification was wrong, not the
 * work**, and the (state,style) data join proposed to fill them (register item A-x) was cancelled.
 *
 * ⚠️ `buildStyleUrl` IS NOT THIS AND SURVIVES — it builds the SUBURB×STYLE address
 * (`/nsw/parramatta-2150/gyms/bjj`, 435 of them). The two were one segment apart, and that
 * near-name is exactly why this tombstone sits here rather than in the register alone.
 *
 * Full account: `docs/state-expansion/vic/remediation-register.md`, 2026-08-16 rows.
 */

/**
 * Build a directory URL for a given location.
 * Format: /[state]/[suburb-slug]/gyms
 */
export function buildDirectoryUrl(suburb, state) {
    return `/${state}/${toAddressedLocationSlug(suburb, state)}/gyms`;
}

/**
 * Build a style-filtered directory URL (path-based canonical).
 * Format: /[state]/[suburb-slug]/gyms/[style-slug]
 */
export function buildStyleUrl(suburb, style, state) {
    return `/${state}/${toAddressedLocationSlug(suburb, state)}/gyms/${toStyleSlug(style)}`;
}

/**
 * Build a gym profile URL.
 * Format: /[state]/[suburb-slug]/gyms/[gymname]-g[stableId]
 *
 * @param gymId - The stable internal ID (not the Google Place ID)
 */
export function buildGymUrl(gymName, gymId, suburb, postcode, state = DEFAULT_STATE) {
    // `postcode` is the GYM's own, and is deliberately NOT the postcode that lands in
    // the URL. It is still accepted and still passed through the normaliser, so the
    // long-standing "Suburb Postcode" call shape keeps working, but the address comes
    // from the suburb map: 61 gym records disagree with their own suburb page's
    // register value, and honouring them here would file those gyms under a suburb
    // slug no directory page is built at. See lib/suburbPostcode.mjs.
    const location = postcode ? `${suburb} ${postcode}` : suburb;
    return `/${state}/${toAddressedLocationSlug(location, state)}/gyms/${toGymSlug(gymName, gymId)}`;
}
